/**
 * The work-item tracker's pure parts (design 13; the crew, design 21; days first and three hours,
 * design 29): the draft the grid edits, the save body built from it, what Add Days/Dates will do,
 * the hour totals, Delay project and the column clipboard. Hours are decimal text on the wire; every
 * sum here is done in integer hundredths and formatted back to two places by string, so
 * `0.10 + 0.20` is `0.30` and never a float's idea of it.
 *
 * A tracker's columns are calendar days — Day 1, Day 2 … — whose number the office sets, and which
 * carry dates once the office puts them on, all at once: a tracker is all days or all dates, and once
 * dated its number of days is fixed (Delay project alone adds to it). Actuals wait for dates, and
 * never travel with a copied column. A crew member has expected and actual hours on each day; the
 * expected are the project manager's plan, typed by hand. The quoted hours are the quote worksheet's,
 * one figure for the job, counted down day by day by the expected hours (D-167).
 *
 * An item's crew is its named technicians and its numbered TBD rows, keyed in the draft as
 * `u:<jobberUserId>` and `t:<n>`. The one rule carried over from the tool this replaces, member for
 * member: an active technician or a TBD row spans every day of the tracker (blank saves as zero),
 * while a technician Jobber has since deactivated stays only on the days that already hold their
 * hours. A TBD row has no actual hours: actuals belong to a named technician.
 *
 * An item has a type, covering every stage of it, and a Quantity the project manager types (design 37):
 * what its % complete divides by, its Planned counts standing in until one is typed. Items of one type can
 * be linked under a head, the one the techs see; their quantities show as one combined figure.
 */

import type { QuotedHoursSource, SaveWorkItemBody, SaveWorkItemCrewMember, SaveWorkItemDay, SaveWorkItemLabour, SetWorkItemGroupBody, WorkDate, WorkItem, WorkItemGroup, WorkItemTechnician, WorkStage, WorkType } from '@unitedos/shared';
import { addDays, formatDayHeading, weekdayOf } from './time.js';

export const MAX_NAME_LENGTH = 200;
/** Days added in one go: the API's ceiling. */
export const MAX_DAYS_PER_ADD = 365;
export const MAX_TBD = 99;

const HOURS = /^[0-9]{1,6}(\.[0-9]{1,2})?$/;
const COUNT = /^[0-9]{1,9}$/;

// ------------------------------------------------------------------ the rows --

/** A crew member's two hours rows, in the order the grid draws them (design 29; the quoted hours went to the worksheet, D-167). */
export const HOUR_ROWS = ['expectedHours', 'actualHours'] as const;
export type HoursKind = (typeof HOUR_ROWS)[number];

/** The rows' words: *hrs*, never *hours* spelled out (Brian, 2026-10-14). */
export const HOUR_LABELS: Readonly<Record<HoursKind, string>> = {
  expectedHours: 'Expected hrs',
  actualHours: 'Actual hrs',
};

// ------------------------------------------------------------------- drafts --

export interface LabourDayDraft {
  readonly expectedHours: string;
  readonly actualHours: string;
}

/** A member of an item's crew as the grid edits it: a named technician or a TBD row, its hours by day. */
export interface CrewDraft {
  /** `u:<jobberUserId>` or `t:<n>`: how the draft, the clipboard and the save name the member. */
  readonly key: string;
  /** The saved crew row; null for a member added in this draft. */
  readonly id: number | null;
  /** The technician; null on a TBD row. */
  readonly jobberUserId: string | null;
  /** TBD n; null on a technician. */
  readonly tbdNumber: number | null;
  /** Set when a saved TBD row was assigned in this draft: the save re-points that row, its hours kept. */
  readonly wasTbdNumber: number | null;
  /** The technician's name, or `TBD n`. */
  readonly name: string;
  /** Spans every day: a TBD row, or a technician active in Jobber. Otherwise it stays only where it has data. */
  readonly active: boolean;
  /** By work date id; absent means blank. */
  readonly days: Readonly<Record<number, LabourDayDraft>>;
}

export interface CountsDraft {
  readonly plannedCount: string;
  readonly actualCount: string;
}

/** A day's live entries on an item (design 36): the units counted, and each tech's hours in hundredths. Read-only. */
export interface CellEntries {
  readonly count: number;
  readonly hours: Readonly<Record<string, number>>;
}

export const NO_ENTRIES: CellEntries = { count: 0, hours: {} };

export interface ItemDraft {
  readonly id: number;
  readonly name: string;
  /** The work type (design 36); null until set. The item covers every stage of it. */
  readonly typeId: number | null;
  /** How many units the item is, as typed (design 37); blank until the project manager types one. */
  readonly quantity: string;
  readonly position: number;
  readonly version: number;
  /** By work date id; absent means blank. */
  readonly counts: Readonly<Record<number, CountsDraft>>;
  /** In order: named technicians and TBD rows. */
  readonly crew: readonly CrewDraft[];
  /**
   * The live entries by work date id (design 36). A cell's text is the total — the office's typed figure
   * plus these — and the save sends the typed part: the text less these.
   */
  readonly entries: Readonly<Record<number, CellEntries>>;
  /** Entries on days the plan does not have: in the Total column only. */
  readonly offPlan: CellEntries;
}

function entriesOf(count: number, rows: readonly { readonly jobberUserId: string; readonly hours: string }[]): CellEntries {
  const hours: Record<string, number> = {};
  for (const r of rows) hours[r.jobberUserId] = hundredths(r.hours);
  return { count, hours };
}

export const BLANK_DAY: LabourDayDraft = { expectedHours: '', actualHours: '' };
export const BLANK_COUNTS: CountsDraft = { plannedCount: '', actualCount: '' };

export const technicianKey = (jobberUserId: string): string => `u:${jobberUserId}`;
export const tbdKey = (n: number): string => `t:${n}`;
export const tbdName = (n: number): string => `TBD ${n}`;

/** Zero shows as blank: a full grid of 0.00 hides the cells that matter. */
function blankIfZero(text: string): string {
  return /^0+(\.0+)?$/.test(text) ? '' : text;
}

/** The item as the grid edits it: strings as typed, the crew in order, each member's hours across the days. */
export function draftFromItem(item: WorkItem): ItemDraft {
  const counts: Record<number, CountsDraft> = {};
  const hours = new Map<number, Record<number, LabourDayDraft>>();
  const entries: Record<number, CellEntries> = {};
  const userOf = new Map(item.crew.map((m) => [m.id, m.jobberUserId]));
  for (const day of item.days) {
    const cell = entriesOf(day.entryCount, day.entryHours);
    if (cell.count !== 0 || day.entryHours.length > 0) entries[day.workDateId] = cell;
    // A cell shows the total: the typed figure plus the day's entries.
    const actualCount = day.actualCount + cell.count;
    if (day.plannedCount !== 0 || actualCount !== 0) {
      counts[day.workDateId] = { plannedCount: blankIfZero(String(day.plannedCount)), actualCount: blankIfZero(String(actualCount)) };
    }
    for (const row of day.labour) {
      const days = hours.get(row.crewId) ?? {};
      const user = userOf.get(row.crewId) ?? null;
      const actual = hundredths(row.actualHours) + (user === null ? 0 : (cell.hours[user] ?? 0));
      days[day.workDateId] = { expectedHours: blankIfZero(row.expectedHours), actualHours: actual === 0 ? '' : formatHundredths(actual) };
      hours.set(row.crewId, days);
    }
    // A technician with entry hours and no typed row that day still shows them.
    for (const m of item.crew) {
      if (m.jobberUserId === null || cell.hours[m.jobberUserId] === undefined) continue;
      const days = hours.get(m.id) ?? {};
      if (days[day.workDateId] === undefined) days[day.workDateId] = { expectedHours: '', actualHours: formatHundredths(cell.hours[m.jobberUserId]!) };
      hours.set(m.id, days);
    }
  }
  const crew = [...item.crew]
    .sort((a, b) => a.position - b.position || a.id - b.id)
    .map((m): CrewDraft => {
      const tbd = m.jobberUserId === null ? (m.tbdNumber ?? 0) : null;
      return {
        key: tbd === null ? technicianKey(m.jobberUserId ?? '') : tbdKey(tbd),
        id: m.id,
        jobberUserId: m.jobberUserId,
        tbdNumber: tbd,
        wasTbdNumber: null,
        name: tbd === null ? (m.name ?? m.jobberUserId ?? '') : tbdName(tbd),
        active: m.active,
        days: hours.get(m.id) ?? {},
      };
    });
  return {
    id: item.id,
    name: item.name,
    typeId: item.typeId,
    quantity: item.quantity === null ? '' : String(item.quantity),
    position: item.position,
    version: item.version,
    counts,
    crew,
    entries,
    offPlan: entriesOf(item.offPlan.count, item.offPlan.hours),
  };
}

/** A technician picked from the roster: on every day, nothing typed yet. */
export function addTechnician(draft: ItemDraft, technician: WorkItemTechnician): ItemDraft {
  const key = technicianKey(technician.jobberUserId);
  if (draft.crew.some((m) => m.key === key)) return draft;
  const member: CrewDraft = { key, id: null, jobberUserId: technician.jobberUserId, tbdNumber: null, wasTbdNumber: null, name: technician.name, active: technician.active, days: {} };
  return { ...draft, crew: [...draft.crew, member] };
}

/** The lowest TBD number the item's crew does not use: remove TBD 1 and the next added is TBD 1. */
export function nextTbdNumber(draft: ItemDraft): number {
  const used = new Set(draft.crew.map((m) => m.tbdNumber).filter((n): n is number => n !== null));
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

/** A TBD row: a technician nobody is named for yet, on every day, nothing typed yet. */
export function addTbd(draft: ItemDraft): ItemDraft {
  const n = nextTbdNumber(draft);
  if (n > MAX_TBD) return draft;
  const member: CrewDraft = { key: tbdKey(n), id: null, jobberUserId: null, tbdNumber: n, wasTbdNumber: null, name: tbdName(n), active: true, days: {} };
  return { ...draft, crew: [...draft.crew, member] };
}

/**
 * A TBD row named: the row becomes the technician's where it stands, its expected hours
 * kept and its actual hours open. A saved TBD row is re-pointed by the save; one added in this draft
 * simply becomes the technician. A technician already on the crew is never a target, so rows never merge.
 */
export function assignTbd(draft: ItemDraft, key: string, technician: WorkItemTechnician): ItemDraft {
  const target = technicianKey(technician.jobberUserId);
  if (draft.crew.some((m) => m.key === target)) return draft;
  return {
    ...draft,
    crew: draft.crew.map((m) =>
      m.key === key && m.tbdNumber !== null
        ? { ...m, key: target, jobberUserId: technician.jobberUserId, tbdNumber: null, wasTbdNumber: m.id !== null ? m.tbdNumber : null, name: technician.name, active: technician.active }
        : m,
    ),
  };
}

export function removeMember(draft: ItemDraft, key: string): ItemDraft {
  return { ...draft, crew: draft.crew.filter((m) => m.key !== key) };
}

export function setCounts(draft: ItemDraft, dateId: number, update: Partial<CountsDraft>): ItemDraft {
  return { ...draft, counts: { ...draft.counts, [dateId]: { ...(draft.counts[dateId] ?? BLANK_COUNTS), ...update } } };
}

export function setLabour(draft: ItemDraft, key: string, dateId: number, update: Partial<LabourDayDraft>): ItemDraft {
  return {
    ...draft,
    crew: draft.crew.map((m) => (m.key === key ? { ...m, days: { ...m.days, [dateId]: { ...(m.days[dateId] ?? BLANK_DAY), ...update } } } : m)),
  };
}

// ---------------------------------------------------------- type and quantity --

/**
 * A type's ordered stages (design 36): every stage an item of it is measured over, never Not Listed, which
 * only a tech's entry takes. None for no type or a type the list no longer has.
 */
export function typeStages(types: readonly WorkType[], typeId: number | null): readonly WorkStage[] {
  if (typeId === null) return [];
  return (types.find((t) => t.id === typeId)?.stages ?? []).filter((s) => !s.notListed);
}

export function setType(draft: ItemDraft, typeId: number | null): ItemDraft {
  return typeId === draft.typeId ? draft : { ...draft, typeId };
}

export function setQuantity(draft: ItemDraft, quantity: string): ItemDraft {
  return { ...draft, quantity };
}

/** `app.work_item.quantity`'s ceiling. */
export const MAX_QUANTITY = 1_000_000;

const QUANTITY = /^\d{1,7}$/;

/** A typed Quantity as the save sends it: null when blank, the number when a whole number in range, otherwise undefined (refused). */
export function normalizeQuantity(text: string): number | null | undefined {
  const t = text.trim().replace(/,/g, '');
  if (t === '') return null;
  if (!QUANTITY.test(t)) return undefined;
  const n = Number(t);
  return n <= MAX_QUANTITY ? n : undefined;
}

/**
 * An item's own quantity (design 37): the typed Quantity, or while none is typed its Planned counts
 * summed — shown muted, worked out rather than typed. Nothing typed and nothing planned is 0.
 */
export function ownQuantity(draft: ItemDraft): { readonly value: number; readonly typed: boolean } {
  const typed = normalizeQuantity(draft.quantity);
  return typeof typed === 'number' ? { value: typed, typed: true } : { value: plannedTotal(draft), typed: false };
}

/** An item's Planned counts summed over every day, a hidden one too. */
export function plannedTotal(draft: ItemDraft): number {
  return sumCounts(Object.values(draft.counts).map((c) => c.plannedCount));
}

// -------------------------------------------------------------------- links --
// Linked work items (design 37): items of one type shown as one piece of work, their quantities one combined
// figure, one of them the head the techs see on the Notes page. The draft never holds a link: a link is saved
// on its own, at once, and the Plan reads itself again.

/** The members of a group, in the Plan's order. */
export function groupMembers(items: readonly WorkItem[], groupId: number): WorkItem[] {
  return items.filter((i) => i.groupId === groupId);
}

/** The units an item's deduct lines take off it (design 37 §7.4): Σ over its change orders' deducts. */
export function deductedOf(item: WorkItem): number {
  return item.deducts.reduce((sum, d) => sum + d.quantity, 0);
}

/**
 * An item's effective quantity (design 37 §7.4): its own quantity — typed, or its Planned counts — less what deductive
 * change orders take off it, never below zero. What its % complete divides by, and what a group adds up.
 */
export function effectiveQuantity(draft: ItemDraft, item: WorkItem): number {
  return Math.max(0, ownQuantity(draft).value - deductedOf(item));
}

/** The Quantity cell's deducts, one line per change order: `− 2 CO-04`. */
export function deductLines(item: WorkItem): string[] {
  return item.deducts.map((d) => `− ${d.quantity} ${d.label}`);
}

/** Their tooltip: `2 taken off by CO-04`, every change order named. */
export function deductTitle(item: WorkItem): string {
  return item.deducts.map((d) => `${d.quantity} taken off by ${d.label}`).join('; ');
}

/** A group's combined quantity: Σ its members' effective quantities, the drafts as typed. */
export function groupQuantity(members: readonly WorkItem[], drafts: Readonly<Record<number, ItemDraft>>): number {
  return members.reduce((sum, m) => sum + effectiveQuantity(drafts[m.id] ?? draftFromItem(m), m), 0);
}

/** An item the link menu offers: one of the same type, saved; the group it is linked in elsewhere, by its head's name. */
export interface LinkCandidate {
  readonly id: number;
  readonly name: string;
  /** Linked in another group: the head of that group, by name. Null when free or in this group. */
  readonly elsewhere: string | null;
}

/**
 * The items the link menu offers for an item (design 37): every item of its saved type, the item first, then the
 * Plan's order. An item linked in another group is offered but cannot be ticked. None when the item has no type.
 */
export function linkCandidates(item: WorkItem, items: readonly WorkItem[], groups: readonly WorkItemGroup[]): LinkCandidate[] {
  if (item.typeId === null) return [];
  const headName = (groupId: number) => items.find((i) => i.id === groups.find((g) => g.id === groupId)?.headId)?.name ?? 'another item';
  return [item, ...items.filter((i) => i.id !== item.id && i.typeId === item.typeId)].map((i) => ({
    id: i.id,
    name: i.name,
    elsewhere: i.groupId !== null && i.groupId !== item.groupId ? headName(i.groupId) : null,
  }));
}

/** The menu's state: the ticked items and the head. */
export interface LinkDraft {
  readonly members: ReadonlySet<number>;
  readonly headId: number | null;
}

/** The menu as it opens: the item's group as saved, or the item alone and its own head. */
export function linkDraftOf(item: WorkItem, items: readonly WorkItem[], groups: readonly WorkItemGroup[]): LinkDraft {
  const group = item.groupId === null ? undefined : groups.find((g) => g.id === item.groupId);
  if (group === undefined) return { members: new Set([item.id]), headId: item.id };
  return { members: new Set(groupMembers(items, group.id).map((m) => m.id)), headId: group.headId };
}

/** A tick or untick; an unticked head hands the choice to the first member left. */
export function toggleLinked(draft: LinkDraft, id: number, on: boolean): LinkDraft {
  const members = new Set(draft.members);
  if (on) members.add(id);
  else members.delete(id);
  const headId = draft.headId !== null && members.has(draft.headId) ? draft.headId : ([...members][0] ?? null);
  return { members, headId };
}

export type LinkBuild = { readonly ok: true; readonly body: SetWorkItemGroupBody } | { readonly ok: false; readonly reason: string };

/** The body for Save, in the Plan's order: at least two items, the head among them. */
export function linkBody(draft: LinkDraft, item: WorkItem, items: readonly WorkItem[], groups: readonly WorkItemGroup[]): LinkBuild {
  const ordered = items.filter((i) => draft.members.has(i.id)).map((i) => i.id);
  if (ordered.length < 2) return { ok: false, reason: 'Tick at least two items to link.' };
  if (draft.headId === null || !draft.members.has(draft.headId)) return { ok: false, reason: 'Choose the item shown to techs.' };
  const group = item.groupId === null ? undefined : groups.find((g) => g.id === item.groupId);
  return { ok: true, body: group === undefined ? { groupId: null, memberIds: ordered, headId: draft.headId } : { groupId: group.id, expectedVersion: group.version, memberIds: ordered, headId: draft.headId } };
}

/** The body for Unlink all. */
export function unlinkBody(group: WorkItemGroup): SetWorkItemGroupBody {
  return { groupId: group.id, expectedVersion: group.version, memberIds: [], headId: null };
}

/** The screen's words for a link's refusal: the API's own sentence, except for a stale read. */
export function linkErrorText(code: string | undefined, fallback: string): string {
  return code === 'stale_work_item_group' ? 'Someone changed these links after you opened them. The Plan has read them again: try once more.' : fallback;
}

// ------------------------------------------------------------------ entries --

/**
 * The office's part of a cell (design 36 §4.4): what is typed less what the entries give. Blank means the
 * office typed nothing. Null when the typed total is below the entries — those change in the breakdown.
 */
export function typedPart(text: string, total: number, fromEntries: number): number | null {
  if (text.trim() === '') return 0;
  return total < fromEntries ? null : total - fromEntries;
}

/** A cell's tooltip once it holds entries: `Includes 8.00 from tech notes. Typing changes the office's part; it cannot go below 8.00`. */
export function entriesTitle(kind: 'count' | 'hours', fromEntries: number): string {
  const figure = kind === 'count' ? String(fromEntries) : formatHundredths(fromEntries);
  return `Includes ${figure} from tech notes. Typing changes the office's part; it cannot go below ${figure}`;
}

/** A member's hours from entries on days the plan does not have, in hundredths. */
export function offPlanHours(draft: ItemDraft, jobberUserId: string | null): number {
  return jobberUserId === null ? 0 : (draft.offPlan.hours[jobberUserId] ?? 0);
}

// ---------------------------------------------------------------- validation --

export type SaveBuild = { readonly ok: true; readonly body: SaveWorkItemBody } | { readonly ok: false; readonly reason: string };

/** Hours as the wire wants them: two places, blank as zero; null when the text is not hours. */
export function normalizeHours(text: string): string | null {
  const t = text.trim();
  if (t === '') return '0.00';
  if (!HOURS.test(t)) return null;
  return formatHundredths(hundredths(t));
}

function normalizeCount(text: string): number | null {
  const t = text.trim();
  if (t === '') return 0;
  return COUNT.test(t) ? Number(t) : null;
}

function hasLabourData(day: LabourDayDraft | undefined): boolean {
  return day !== undefined && HOUR_ROWS.some((k) => day[k].trim() !== '');
}

/**
 * The save body, or the first reason it cannot be sent — the parser's and the function's rules applied
 * here first so a refusal never leaves the browser. An active technician or a TBD row saves on every day
 * (blanks as zero); an inactive technician only where their cell holds something. Actuals go only on a
 * dated day and never on a TBD row. The whole crew goes, in order, with an assigned TBD row naming the
 * number it was.
 */
export function buildSaveBody(draft: ItemDraft, dates: readonly WorkDate[]): SaveBuild {
  const name = draft.name.trim();
  if (name.length === 0) return { ok: false, reason: 'Enter a name for the work item.' };
  if (name.length > MAX_NAME_LENGTH) return { ok: false, reason: `A work item name is at most ${MAX_NAME_LENGTH} characters.` };

  const crew: SaveWorkItemCrewMember[] = draft.crew.map((m) =>
    m.jobberUserId !== null
      ? m.wasTbdNumber !== null
        ? { jobberUserId: m.jobberUserId, wasTbdNumber: m.wasTbdNumber }
        : { jobberUserId: m.jobberUserId }
      : { tbdNumber: m.tbdNumber ?? 0 },
  );

  const days: SaveWorkItemDay[] = [];
  for (const date of dates) {
    const where = dayName(date);
    const counts = draft.counts[date.id] ?? BLANK_COUNTS;
    const plannedCount = normalizeCount(counts.plannedCount);
    const totalCount = normalizeCount(counts.actualCount);
    if (plannedCount === null || totalCount === null) return { ok: false, reason: `Counts on ${where} must be whole numbers.` };
    const cell = draft.entries[date.id] ?? NO_ENTRIES;
    const typedCount = typedPart(counts.actualCount, totalCount, cell.count);
    if (typedCount === null) return { ok: false, reason: `The actual count on ${where} cannot be below the ${cell.count} from tech notes. Change those in the breakdown.` };
    const actualCount = typedCount;
    if (date.date === null && actualCount !== 0) return { ok: false, reason: `${where} has no date yet, so it takes no actual count. Put dates on the days first.` };

    const labour: SaveWorkItemLabour[] = [];
    for (const m of draft.crew) {
      const day = m.days[date.id];
      if (!m.active && !hasLabourData(day)) continue;
      const d = day ?? BLANK_DAY;
      const expectedHours = normalizeHours(d.expectedHours);
      const totalHours = normalizeHours(d.actualHours);
      if (expectedHours === null || totalHours === null) return { ok: false, reason: `Hours for ${m.name} on ${where} must be a number with at most two decimal places.` };
      const fromNotes = m.jobberUserId === null ? 0 : (cell.hours[m.jobberUserId] ?? 0);
      const typedHours = typedPart(d.actualHours, hundredths(totalHours), fromNotes);
      if (typedHours === null) return { ok: false, reason: `${m.name}'s actual hours on ${where} cannot be below the ${formatHundredths(fromNotes)} from tech notes. Change those in the breakdown.` };
      const actualHours = formatHundredths(typedHours);
      if (hundredths(actualHours) !== 0) {
        if (m.jobberUserId === null) return { ok: false, reason: `${m.name} has no actual hours. Assign a technician first.` };
        if (date.date === null) return { ok: false, reason: `${where} has no date yet, so it takes no actual hours. Put dates on the days first.` };
      }
      if (m.jobberUserId !== null) labour.push({ jobberUserId: m.jobberUserId, expectedHours, actualHours });
      else labour.push({ tbdNumber: m.tbdNumber ?? 0, expectedHours, actualHours: '0.00' });
    }
    days.push({ workDateId: date.id, plannedCount, actualCount, labour });
  }
  const quantity = normalizeQuantity(draft.quantity);
  if (quantity === undefined) return { ok: false, reason: `Quantity must be a whole number from 0 to ${MAX_QUANTITY.toLocaleString('en-US')}.` };
  return { ok: true, body: { name, position: draft.position, expectedVersion: draft.version, typeId: draft.typeId, quantity, crew, days } };
}

// -------------------------------------------------------------------- totals --

/** "12.5" → 1250, "7" → 700. Only ever called on text the HOURS pattern admits. */
export function hundredths(text: string): number {
  const [whole, frac = ''] = text.trim().split('.') as [string, string?];
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2));
}

/** 1250 → "12.50". Integer arithmetic only. */
export function formatHundredths(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  return `${sign}${whole}.${frac < 10 ? '0' : ''}${frac}`;
}

/** The sum of hour texts, in hundredths; text that is not hours counts as nothing. */
export function sumHours(texts: readonly string[]): number {
  let total = 0;
  for (const t of texts) if (HOURS.test(t.trim())) total += hundredths(t);
  return total;
}

/** The sum of count texts; text that is not a whole number counts as nothing. */
export function sumCounts(texts: readonly string[]): number {
  let total = 0;
  for (const t of texts) if (COUNT.test(t.trim())) total += Number(t.trim());
  return total;
}

/** The dated days on or before `today` (YYYY-MM-DD, the office day): what "to date" sums over. None while the tracker is days only. */
export function datesThrough(dates: readonly WorkDate[], today: string): WorkDate[] {
  return dates.filter((d) => d.date !== null && d.date <= today);
}

/** One hours row summed: by day, across every day, and to date — in hundredths. */
export interface RowTotals {
  readonly byDate: Readonly<Record<number, number>>;
  readonly total: number;
  readonly toDate: number;
}

export type HoursTotals = Readonly<Record<HoursKind, RowTotals>>;

/** The item's expected and actual hours, each summed over its crew. */
export function itemTotals(draft: ItemDraft, dates: readonly WorkDate[], today: string): HoursTotals {
  const row = (kind: HoursKind): RowTotals => {
    const byDate: Record<number, number> = {};
    let total = 0;
    let toDate = 0;
    for (const date of dates) {
      const sum = sumHours(draft.crew.map((m) => m.days[date.id]?.[kind] ?? ''));
      byDate[date.id] = sum;
      total += sum;
      if (date.date !== null && date.date <= today) toDate += sum;
    }
    if (kind === 'actualHours') total += Object.values(draft.offPlan.hours).reduce((a, b) => a + b, 0);
    return { byDate, total, toDate };
  };
  return { expectedHours: row('expectedHours'), actualHours: row('actualHours') };
}

/** Every item's hours on each day, across the tracker and to date, in hundredths. */
export function trackerTotals(drafts: readonly ItemDraft[], dates: readonly WorkDate[], today: string): HoursTotals {
  const all = drafts.map((d) => itemTotals(d, dates, today));
  const row = (kind: HoursKind): RowTotals => {
    const byDate: Record<number, number> = {};
    for (const date of dates) byDate[date.id] = all.reduce((s, t) => s + (t[kind].byDate[date.id] ?? 0), 0);
    return { byDate, total: all.reduce((s, t) => s + t[kind].total, 0), toDate: all.reduce((s, t) => s + t[kind].toDate, 0) };
  };
  return { expectedHours: row('expectedHours'), actualHours: row('actualHours') };
}

/** Quoted hrs remaining: what is left after each day, and after the last (D-167). In hundredths; below zero is an overrun. */
export interface QuotedRemaining {
  readonly byDate: Readonly<Record<number, number>>;
  readonly end: number;
}

/** Sources named in the footer before *+N more* (design 37 §7.5, the reviewer's finding 15). */
export const QUOTED_SOURCES_SHOWN = 3;

/** Hours for a sentence: "220.00" → "220", "12.50" → "12.5"; a signed figure keeps its sign as a minus. */
function hoursWords(text: string): string {
  const t = text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
  return t.startsWith('-') ? `−${t.slice(1)}` : t;
}

/**
 * The footer's words for the quoted hours (design 37 §7.5): with only the original, *(240 Quoted Hrs.)*;
 * with converted change orders, the sources after it — *(240 Quoted Hrs.: 220 original · +32 CO-01 ·
 * −12 CO-04)* — the first three change orders named, the rest *+2 more*, every source in the title.
 */
export function quotedHoursText(quotedHours: string | null, sources: readonly QuotedHoursSource[]): { readonly text: string; readonly title: string | undefined } {
  if (quotedHours === null) return { text: '(No quote worksheet)', title: undefined };
  const total = hoursWords(quotedHours);
  const changeOrders = sources.filter((s) => s.label !== 'original');
  if (changeOrders.length === 0) return { text: `(${total} Quoted Hrs.)`, title: undefined };
  const word = (s: QuotedHoursSource) => {
    const h = hoursWords(s.hours);
    return s.label === 'original' ? `${h} original` : `${h.startsWith('−') ? h : `+${h}`} ${s.label}`;
  };
  const original = sources.find((s) => s.label === 'original');
  const shown = [...(original ? [original] : []), ...changeOrders.slice(0, QUOTED_SOURCES_SHOWN)].map(word);
  const more = changeOrders.length - QUOTED_SOURCES_SHOWN;
  return {
    text: `(${total} Quoted Hrs.: ${shown.join(' · ')}${more > 0 ? ` · +${more} more` : ''})`,
    title: `The quote worksheet's hours and each converted change order's: ${sources.map(word).join(', ')}`,
  };
}

/**
 * The worksheet's quoted hours counted down by the plan (D-167): each day is the day before's remainder
 * less that day's expected hours across every work item, Day 1 starting from the quoted hours. A hidden
 * day counts, as it does in every total (D-096). Null when the job has no quoted hours to count down —
 * no quote, or no worksheet on it — or the figure is not two-place decimal text.
 */
export function quotedRemaining(quotedHours: string | null, dates: readonly WorkDate[], expected: RowTotals): QuotedRemaining | null {
  if (quotedHours === null || !/^[0-9]+(\.[0-9]{1,2})?$/.test(quotedHours.trim())) return null;
  let left = hundredths(quotedHours);
  const byDate: Record<number, number> = {};
  for (const d of orderedDays(dates)) {
    left -= expected.byDate[d.id] ?? 0;
    byDate[d.id] = left;
  }
  return { byDate, end: left };
}

// ---------------------------------------------------------------------- days --

/** Does the tracker have dates? A tracker's days are all dated or none (design 29). */
export function isTrackerDated(dates: readonly WorkDate[]): boolean {
  return dates.some((d) => d.date !== null);
}

/** How a message names a day: `Mon, Oct 20` once dated, `Day 3` before. */
export function dayName(d: WorkDate): string {
  return d.date !== null ? formatDayHeading(d.date) : `Day ${d.day}`;
}

export function isWeekend(date: string): boolean {
  const wd = weekdayOf(date);
  return wd === 0 || wd === 6;
}

export interface SetDaysPreview {
  /** The plan's number of days after the change. */
  readonly days: number;
  readonly added: number;
  /** The days that would go, and how many figures they hold; null when none go. */
  readonly removed: { readonly fromDay: number; readonly days: number; readonly entries: number } | null;
  /** The first and last date the plan would have, or null when it is not dated. */
  readonly firstDate: string | null;
  readonly lastDate: string | null;
  /** The button's words: what it will do. */
  readonly action: string;
}

const daysText = (n: number): string => (n === 1 ? '1 day' : `${n} days`);

/** The figures a day holds across the drafts: each count, and each crew member's hours — what `app.set_work_days` counts. */
function entriesOn(drafts: Readonly<Record<number, ItemDraft>>, dateId: number): number {
  let n = 0;
  for (const draft of Object.values(drafts)) {
    const c = draft.counts[dateId];
    if (c) n += (sumCounts([c.plannedCount]) > 0 ? 1 : 0) + (sumCounts([c.actualCount]) > 0 ? 1 : 0);
    for (const m of draft.crew) {
      const d = m.days[dateId];
      if (d && HOUR_ROWS.some((k) => sumHours([d[k]]) > 0)) n += 1;
    }
  }
  return n;
}

/**
 * What Add Days/Dates will do, by the rule `app.set_work_days` keeps (design 29, second round): the plan
 * gets `count` calendar days — more are numbered on, fewer remove the last ones with whatever they hold —
 * and with a start every day is dated, Day 1 on the start, weekends included. Once the plan has dates
 * its number of days is fixed.
 */
export function setDaysPreview(
  dates: readonly WorkDate[],
  drafts: Readonly<Record<number, ItemDraft>>,
  count: number,
  start: string | null,
): { readonly ok: true; readonly preview: SetDaysPreview } | { readonly ok: false; readonly reason: string } {
  if (isTrackerDated(dates)) return { ok: false, reason: 'The days have dates. Delay project moves the plan.' };
  if (!Number.isInteger(count) || count < 1 || count > MAX_DAYS_PER_ADD) return { ok: false, reason: `Enter a number of days from 1 to ${MAX_DAYS_PER_ADD}.` };
  if (start !== null && !/^\d{4}-\d{2}-\d{2}$/.test(start)) return { ok: false, reason: 'Pick a start date.' };
  const ordered = ascending(dates);
  const current = ordered.length;
  if (count === current && start === null) return { ok: false, reason: 'The plan already has that many days.' };

  const going = ordered.filter((d) => d.day > count);
  const removed = going.length === 0 ? null : { fromDay: count + 1, days: going.length, entries: going.reduce((n, d) => n + entriesOn(drafts, d.id), 0) };
  const firstDate = start;
  const lastDate = start === null ? null : addDays(start, count - 1);
  const withDates = start === null ? '' : ' with dates';
  const action =
    current === 0
      ? `Add ${daysText(count)}${withDates}`
      : count === current
        ? `Put dates on ${daysText(count)}`
        : removed !== null && removed.entries > 0
          ? `Change to ${daysText(count)}${withDates} and remove ${removed.entries === 1 ? '1 entry' : `${removed.entries} entries`}`
          : `Change to ${daysText(count)}${withDates}`;
  return { ok: true, preview: { days: count, added: Math.max(0, count - current), removed, firstDate, lastDate, action } };
}

// ------------------------------------------------------------- the heading --

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** A date column's heading: the weekday on top, `M/D/YY` under it (Brian, 2026-10-02) — as wide as the four digits the column holds. */
export function dateHeading(key: string): { readonly weekday: string; readonly date: string } {
  const [y = '', m = '', d = ''] = key.split('-');
  return { weekday: WEEKDAYS[weekdayOf(key)] ?? '', date: `${Number(m)}/${Number(d)}/${y.slice(-2)}` };
}

/** A column's heading on two lines: `Mon` over `10/20/26` once dated, `Day` over `3` before (design 29). */
export function columnHeading(d: WorkDate): { readonly top: string; readonly bottom: string } {
  if (d.date === null) return { top: 'Day', bottom: String(d.day) };
  const h = dateHeading(d.date);
  return { top: h.weekday, bottom: h.date };
}

// ---------------------------------------------------------------- what is lost --

/** Does the saved item hold anything a delete would lose: a count, an hour? */
export function itemHasData(item: WorkItem): boolean {
  return item.days.some((d) => d.plannedCount !== 0 || d.actualCount !== 0 || d.labour.length > 0);
}

/** How many saved entries a delete would lose: non-zero counts and crew members' hour rows. */
export function itemEntryCount(item: WorkItem): number {
  return item.days.reduce((n, d) => n + (d.plannedCount !== 0 ? 1 : 0) + (d.actualCount !== 0 ? 1 : 0) + d.labour.length, 0);
}

/** How many of the member's cells in the draft hold hours, of any row: what removing the row loses. */
export function technicianEntryCount(member: CrewDraft): number {
  return Object.values(member.days).filter((day) => hasLabourData(day)).length;
}

// ------------------------------------------------------------ add work item --
// Add work item starts as TBD technicians (design 29): as many as the previous work item had people,
// 1 for the first. Naming a technician fills a TBD place; dropping one gives the place back.

export interface CrewPick {
  /** The TBD rows the item will have. */
  readonly tbd: number;
  /** The named technicians, by Jobber user id. */
  readonly named: ReadonlySet<string>;
}

/** The crew size a new work item starts from: the last added this visit, else the tracker's last item's, else 1. */
export function defaultCrewSize(lastAdded: number | null, items: readonly ItemDraft[]): number {
  if (lastAdded !== null) return lastAdded;
  const last = items[items.length - 1];
  return last ? last.crew.length : 1;
}

/** A technician ticked or unticked: ticking takes a TBD place while there is one, unticking gives one back. */
export function toggleNamed(pick: CrewPick, jobberUserId: string, on: boolean): CrewPick {
  const named = new Set(pick.named);
  if (on === named.has(jobberUserId)) return pick;
  if (on) {
    named.add(jobberUserId);
    return { named, tbd: Math.max(0, pick.tbd - 1) };
  }
  named.delete(jobberUserId);
  return { named, tbd: Math.min(MAX_TBD, pick.tbd + 1) };
}

/** The technicians are not known after all: every named place goes back to TBD. */
export function forgetNamed(pick: CrewPick): CrewPick {
  return { named: new Set(), tbd: Math.min(MAX_TBD, pick.tbd + pick.named.size) };
}

// ------------------------------------------------------------- hidden dates --
// A day is hidden, never deleted (D-096): as a spreadsheet's hidden column it keeps every cell,
// stays in every total, every save, every delay and every paste, and is simply not drawn. The
// grid draws a narrow marker where a run of hidden days sits; clicking it shows them again.

export type GridColumn = { readonly kind: 'date'; readonly date: WorkDate } | { readonly kind: 'hidden'; readonly dates: readonly WorkDate[] };

/** The columns the grid draws, in day order: every shown day, and one marker per run of hidden ones. */
export function gridColumns(dates: readonly WorkDate[]): GridColumn[] {
  const columns: GridColumn[] = [];
  let run: WorkDate[] = [];
  for (const date of ascending(dates)) {
    if (date.hidden) {
      run.push(date);
      continue;
    }
    if (run.length > 0) columns.push({ kind: 'hidden', dates: run });
    run = [];
    columns.push({ kind: 'date', date });
  }
  if (run.length > 0) columns.push({ kind: 'hidden', dates: run });
  return columns;
}

/** The hidden days, in order. */
export function hiddenDates(dates: readonly WorkDate[]): WorkDate[] {
  return ascending(dates).filter((d) => d.hidden);
}

// --------------------------------------------------------------- job number --

/** A typed job number: digits, an optional leading `#`, spaces around; null for anything else. */
export function parseJobNumber(text: string): number | null {
  const t = text.trim().replace(/^#\s*/, '');
  if (!/^[0-9]{1,9}$/.test(t)) return null;
  const n = Number(t);
  return n > 0 ? n : null;
}

// ------------------------------------------------------------------ messages --

/** The screen's words for the API's refusal codes. */
export function saveErrorText(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'stale_work_item':
      return 'Someone else saved this work item after you opened it. Reload it before editing again.';
    case 'duplicate_name':
      return 'Another work item on this job already has that name.';
    case 'bad_date':
      return 'A day on this plan was removed. Reload the tab.';
    case 'undated_actual':
      return 'Actuals go on a day with a date. Put dates on the days first.';
    case 'already_dated':
      return 'The days already have dates. Reload the tab.';
    case 'bad_crew':
      return 'The crew on this work item changed since you opened it. Reload it before editing again.';
    case 'tbd_actual':
      return 'A TBD row has no actual hours. Assign a technician first.';
    case 'not_technician':
      return 'A technician on this item is no longer active in Jobber. Remove the row or reload the tab.';
    case 'bad_type':
      return 'That work type was removed in Settings. Reload the tab.';
    case 'grouped_type':
      return 'Linked items share one type. Unlink this one to change its type.';
    default:
      return fallback;
  }
}

// ------------------------------------------------------------- delay project --
// The job slipped: every planned figure — the planned count and the expected hours — on and after
// a chosen day moves N days later (D-095, design 29). Actual figures stay on the day
// they happened. It moves by columns: a dated tracker holds consecutive calendar days, so N days is N
// calendar days (an older tracker with gaps moves by column). The days the move needs are added at the
// end first, by the caller (`appendWorkDays`, the one way a dated plan grows); the
// move itself is a pure translation of the drafts.

/** The days in order. Day numbers rise with dates, so this is date order too. */
function ascending(dates: readonly WorkDate[]): WorkDate[] {
  return [...dates].sort((a, b) => a.day - b.day || a.id - b.id);
}

/** The first day on or after a date, for Delay project on a dated tracker; null when the plan ends before it. */
export function firstDayFrom(dates: readonly WorkDate[], key: string): number | null {
  return ascending(dates).find((d) => d.date !== null && d.date >= key)?.day ?? null;
}

/** The days from `fromDay` on, in order, and the day each one's planned figures land on — null where the plan has no such day yet. */
export function delayMoves(dates: readonly WorkDate[], fromDay: number, days: number): readonly { readonly source: WorkDate; readonly target: WorkDate | null }[] {
  const order = ascending(dates);
  return order
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => d.day >= fromDay)
    .map(({ d, i }) => ({ source: d, target: order[i + days] ?? null }));
}

/** The days that hold a planned figure in some draft: the only ones a move has to land somewhere. */
function datesWithPlan(drafts: Readonly<Record<number, ItemDraft>>): ReadonlySet<number> {
  const used = new Set<number>();
  for (const draft of Object.values(drafts)) {
    for (const [id, c] of Object.entries(draft.counts)) if (c.plannedCount.trim() !== '') used.add(Number(id));
    for (const m of draft.crew) for (const [id, day] of Object.entries(m.days)) if (day.expectedHours.trim() !== '') used.add(Number(id));
  }
  return used;
}

/** How many days the move needs added at the end, for the sources that hold a planned figure; an empty day needs no landing. */
export function delayDaysNeeded(dates: readonly WorkDate[], fromDay: number, days: number, drafts: Readonly<Record<number, ItemDraft>>): number {
  const order = ascending(dates);
  const used = datesWithPlan(drafts);
  let lastIndex = -1;
  order.forEach((d, i) => {
    if (d.day >= fromDay && used.has(d.id)) lastIndex = i;
  });
  return lastIndex < 0 ? 0 : Math.max(0, lastIndex + days - (order.length - 1));
}

export type DelayResult = { readonly ok: true; readonly drafts: Readonly<Record<number, ItemDraft>>; readonly changed: ReadonlySet<number> } | { readonly ok: false; readonly reason: string };

/**
 * Every planned count and expected hours on or after `fromDay` moved `days` days later, as one
 * translation (a day that is both a source and a target gets the figure from behind it, not its own).
 * Actual counts and actual hours stay. Every landing day must already be on the plan
 * (`delayDaysNeeded` says how many to add first).
 */
export function delayPlan(drafts: Readonly<Record<number, ItemDraft>>, dates: readonly WorkDate[], fromDay: number, days: number): DelayResult {
  if (!Number.isInteger(fromDay) || fromDay < 1) return { ok: false, reason: 'Pick the first day to move.' };
  if (!Number.isInteger(days) || days < 1 || days > MAX_DAYS_PER_ADD) return { ok: false, reason: `Days must be a whole number from 1 to ${MAX_DAYS_PER_ADD}.` };
  const moves = delayMoves(dates, fromDay, days);
  if (moves.length === 0) return { ok: false, reason: 'The plan has no days from there on.' };
  const used = datesWithPlan(drafts);
  const missing = moves.find((m) => used.has(m.source.id) && m.target === null);
  if (missing) return { ok: false, reason: `The plan needs more days after ${dayName(missing.source)}. Add them and try again.` };
  // A source with nothing planned has nowhere to land and nothing to carry; it is only cleared.
  const map = moves.filter((m) => m.target !== null).map((m) => ({ fromId: m.source.id, toId: m.target!.id }));
  const cleared = moves.map((m) => m.source.id);

  const next: Record<number, ItemDraft> = {};
  const changed = new Set<number>();
  for (const [idText, draft] of Object.entries(drafts)) {
    const id = Number(idText);
    // Read every moving figure first, then clear the sources, then write the targets.
    const movingCounts = map.map(({ fromId, toId }) => ({ toId, value: draft.counts[fromId]?.plannedCount ?? '' }));
    const counts: Record<number, CountsDraft> = { ...draft.counts };
    for (const fromId of cleared) if (counts[fromId]) counts[fromId] = { ...counts[fromId], plannedCount: '' };
    // A target on the plan is always a source too (it lies after `fromDay`), so it was cleared above; only a figure needs writing.
    for (const { toId, value } of movingCounts) if (value !== '') counts[toId] = { ...(counts[toId] ?? BLANK_COUNTS), plannedCount: value };

    const crew = draft.crew.map((m) => {
      const moving = map.map(({ fromId, toId }) => ({ toId, expectedHours: m.days[fromId]?.expectedHours ?? '' }));
      const daysNext: Record<number, LabourDayDraft> = { ...m.days };
      for (const fromId of cleared) if (daysNext[fromId]) daysNext[fromId] = { ...daysNext[fromId], expectedHours: '' };
      for (const { toId, expectedHours } of moving) {
        if (expectedHours !== '') daysNext[toId] = { ...(daysNext[toId] ?? BLANK_DAY), expectedHours };
      }
      return { ...m, days: daysNext };
    });

    const moved: ItemDraft = { ...draft, counts, crew };
    if (JSON.stringify(moved) !== JSON.stringify(draft)) changed.add(id);
    next[id] = moved;
  }
  return { ok: true, drafts: next, changed };
}

// ------------------------------------------------------------- column copy --
// Day columns selected, copied, cut and pasted as in a spreadsheet (D-095): click a heading,
// shift-click for a range, Ctrl/Cmd-click to add or drop one, drag across headings; copy takes the
// plan the days hold across every item and crew member — the planned count and the expected
// hours, never an actual; paste writes it from the first selected day forward. In the drafts until Save all.

export interface ColumnSelection {
  /** Work date ids, in no particular order. */
  readonly ids: ReadonlySet<number>;
  /** Where a shift-click range starts: the last plain click. */
  readonly anchor: number | null;
}

export const NO_SELECTION: ColumnSelection = { ids: new Set(), anchor: null };

/** The days in order — the order a range and a paste run in. */
export function orderedDays(dates: readonly WorkDate[]): WorkDate[] {
  return ascending(dates);
}

/**
 * A click on a day heading: plain selects that column alone; `range` (shift) selects from the
 * anchor to it; `toggle` (Ctrl/Cmd) adds or drops it. The anchor moves on a plain or toggle click.
 */
export function selectColumn(current: ColumnSelection, dates: readonly WorkDate[], id: number, mode: 'plain' | 'range' | 'toggle'): ColumnSelection {
  if (mode === 'toggle') {
    const ids = new Set(current.ids);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    return { ids, anchor: id };
  }
  if (mode === 'range' && current.anchor !== null) {
    // A range runs over the shown days: a hidden day between two selected ones is not selected.
    const order = ascending(dates).filter((d) => !d.hidden).map((d) => d.id);
    const a = order.indexOf(current.anchor);
    const b = order.indexOf(id);
    if (a >= 0 && b >= 0) return { ids: new Set(order.slice(Math.min(a, b), Math.max(a, b) + 1)), anchor: current.anchor };
  }
  return { ids: new Set([id]), anchor: id };
}

/** The selection with the ids no longer on the plan, or hidden since, dropped. */
export function pruneSelection(current: ColumnSelection, dates: readonly WorkDate[]): ColumnSelection {
  const have = new Set(dates.filter((d) => !d.hidden).map((d) => d.id));
  const ids = new Set([...current.ids].filter((id) => have.has(id)));
  if (ids.size === current.ids.size) return current;
  return { ids, anchor: current.anchor !== null && have.has(current.anchor) ? current.anchor : null };
}

/** A crew member's plan on a day: what a copied column carries for them. */
export interface PlanHours {
  readonly expectedHours: string;
}

/** A copied column: the plan only — the planned count and the expected hours. Actuals belong to the day they happened and never travel (Brian, 2026-10-13). */
export interface ColumnClipboard {
  readonly sourceDateId: number;
  /** By work item id; a member's plan by its key. */
  readonly items: Readonly<Record<number, { readonly plannedCount: string; readonly labour: Readonly<Record<string, PlanHours>> }>>;
}

export function copyColumn(drafts: Readonly<Record<number, ItemDraft>>, dateId: number): ColumnClipboard {
  const items: Record<number, { plannedCount: string; labour: Record<string, PlanHours> }> = {};
  for (const [idText, draft] of Object.entries(drafts)) {
    const labour: Record<string, PlanHours> = {};
    for (const m of draft.crew) {
      const d = m.days[dateId] ?? BLANK_DAY;
      labour[m.key] = { expectedHours: d.expectedHours };
    }
    items[Number(idText)] = { plannedCount: (draft.counts[dateId] ?? BLANK_COUNTS).plannedCount, labour };
  }
  return { sourceDateId: dateId, items };
}

/** The selected columns copied, in day order. */
export function copyColumns(drafts: Readonly<Record<number, ItemDraft>>, dates: readonly WorkDate[], ids: ReadonlySet<number>): ColumnClipboard[] {
  return ascending(dates)
    .filter((d) => ids.has(d.id))
    .map((d) => copyColumn(drafts, d.id));
}

/**
 * The clipboard's plan written onto `dateId`, item by item and member by member; an item or member no
 * longer there is skipped. The day's actuals are left as they are.
 */
export function pasteColumn(
  drafts: Readonly<Record<number, ItemDraft>>,
  clipboard: ColumnClipboard,
  dateId: number,
): { readonly drafts: Readonly<Record<number, ItemDraft>>; readonly changed: ReadonlySet<number> } {
  const next: Record<number, ItemDraft> = { ...drafts };
  const changed = new Set<number>();
  for (const [idText, cells] of Object.entries(clipboard.items)) {
    const id = Number(idText);
    const draft = drafts[id];
    if (!draft) continue;
    let pasted = setCounts(draft, dateId, { plannedCount: cells.plannedCount });
    for (const m of draft.crew) {
      const plan = cells.labour[m.key];
      if (plan) pasted = setLabour(pasted, m.key, dateId, plan);
    }
    if (JSON.stringify(pasted) !== JSON.stringify(draft)) {
      next[id] = pasted;
      changed.add(id);
    }
  }
  return { drafts: next, changed };
}

/**
 * Several copied columns pasted from `firstDateId` forward along the plan's days, as a spreadsheet
 * fills to the right; columns that run past the last day are not pasted and counted.
 */
export function pasteColumns(
  drafts: Readonly<Record<number, ItemDraft>>,
  clipboard: readonly ColumnClipboard[],
  dates: readonly WorkDate[],
  firstDateId: number,
): { readonly drafts: Readonly<Record<number, ItemDraft>>; readonly changed: ReadonlySet<number>; readonly pasted: number; readonly didNotFit: number } {
  const order = ascending(dates);
  const start = order.findIndex((d) => d.id === firstDateId);
  if (start < 0) return { drafts, changed: new Set(), pasted: 0, didNotFit: clipboard.length };
  let next = drafts;
  const changed = new Set<number>();
  let pasted = 0;
  clipboard.forEach((clip, i) => {
    const target = order[start + i];
    if (!target) return;
    const r = pasteColumn(next, clip, target.id);
    next = r.drafts;
    for (const id of r.changed) changed.add(id);
    pasted += 1;
  });
  return { drafts: next, changed, pasted, didNotFit: clipboard.length - pasted };
}

/**
 * The cells on `dateId` blank, across every item and crew member: everything (Clear), or the plan only
 * (`planOnly` — the cut half of cut-and-paste, which never takes what a copy does not carry).
 */
export function clearColumn(
  drafts: Readonly<Record<number, ItemDraft>>,
  dateId: number,
  options: { readonly planOnly?: boolean } = {},
): { readonly drafts: Readonly<Record<number, ItemDraft>>; readonly changed: ReadonlySet<number> } {
  const next: Record<number, ItemDraft> = { ...drafts };
  const changed = new Set<number>();
  for (const [idText, draft] of Object.entries(drafts)) {
    const id = Number(idText);
    let cleared = setCounts(draft, dateId, options.planOnly ? { plannedCount: '' } : BLANK_COUNTS);
    for (const m of draft.crew) cleared = setLabour(cleared, m.key, dateId, options.planOnly ? { expectedHours: '' } : BLANK_DAY);
    if (JSON.stringify(cleared) !== JSON.stringify(draft)) {
      next[id] = cleared;
      changed.add(id);
    }
  }
  return { drafts: next, changed };
}

export function clearColumns(
  drafts: Readonly<Record<number, ItemDraft>>,
  ids: ReadonlySet<number>,
  options: { readonly planOnly?: boolean } = {},
): { readonly drafts: Readonly<Record<number, ItemDraft>>; readonly changed: ReadonlySet<number> } {
  let next = drafts;
  const changed = new Set<number>();
  for (const id of ids) {
    const r = clearColumn(next, id, options);
    next = r.drafts;
    for (const c of r.changed) changed.add(c);
  }
  return { drafts: next, changed };
}
